import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { Avatar } from "../components/AppIcon";
import { Icon } from "../components/Icon";
import { Page, TopBar } from "../components/Layout";
import { ListControls, LoadMore } from "../components/ListControls";
import { api } from "../lib/api";
import { shareLink } from "../lib/platform";
import { useToast } from "../lib/toast";
import type { Friendship, Invite } from "../lib/types";
import { usePaged } from "../lib/usePaged";

function status(inv: Invite): { label: string; tone: string } {
  if (inv.revoked) return { label: "Revoked", tone: "" };
  if (inv.uses >= inv.max_uses) return { label: inv.max_uses === 1 ? "Joined" : "Used up", tone: "ok" };
  if (inv.expires_at && new Date(inv.expires_at) < new Date()) return { label: "Expired", tone: "" };
  return { label: "Pending", tone: "warn" };
}

function ago(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
}

export function People() {
  const toast = useToast();
  const invites = usePaged<Invite>("/api/invites", { sort: "-created_at", limit: 20 });
  const friends = usePaged<Friendship>("/api/connections", { sort: "display_name", limit: 20, extra: { status: "friends" } });
  const incoming = usePaged<Friendship>("/api/connections", { sort: "-created_at", limit: 20, extra: { status: "incoming" } });
  const outgoing = usePaged<Friendship>("/api/connections", { sort: "-created_at", limit: 20, extra: { status: "outgoing" } });
  const reloadFriends = () => { friends.reload(); incoming.reload(); outgoing.reload(); };
  const answer = async (f: Friendship, accept: boolean) => {
    try {
      await api(accept ? `/api/connections/${f.id}/accept` : `/api/connections/${f.id}`, { method: accept ? "POST" : "DELETE" });
      toast(accept ? `You and ${f.user.display_name} are now friends` : "Request removed", accept ? "success" : undefined);
      reloadFriends();
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };
  const joined = usePaged<{ id: string; display_name: string; handle: string; avatar_url: string }>("/api/invites/joined", { sort: "-created_at", limit: 20 });
  const [email, setEmail] = useState("");
  const [link, setLink] = useState<Invite | null>(null);
  const [busy, setBusy] = useState(false);

  const share = async (inv: Invite) => {
    if (!(await shareLink({ title: "Join me on Omni", text: inv.note || "Here's an invite to Omni", url: inv.url }))) toast("Invite link copied");
  };

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const inv = await api<Invite>("/api/invites", { body: { email: email || null, note: "" } });
      setEmail("");
      invites.reload();
      await share(inv);
    } catch (err) {
      toast((err as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };

  const makeLink = async () => {
    const inv = await api<Invite>("/api/invites", { body: { max_uses: 10, note: "Shared link" } });
    setLink(inv);
    invites.reload();
  };

  return (
    <>
      <TopBar title="People" />
      <Page>
        {incoming.total > 0 && (
          <>
            <span className="lbl">Friend requests</span>
            <div className="card flush">
              {incoming.items.map((f) => (
                <div key={f.id} className="list-row" style={{ minHeight: 64 }}>
                  <Link to={`/u/${f.user.handle}`}><Avatar name={f.user.display_name} url={f.user.avatar_url} /></Link>
                  <Link to={`/u/${f.user.handle}`} className="grow" style={{ minWidth: 0, color: "inherit" }}>{f.user.display_name}<span className="sub">Wants to be friends · {ago(f.created_at)}</span></Link>
                  <button className="btn btn-sm btn-primary" onClick={() => answer(f, true)}>Accept</button>
                  <button className="btn btn-sm btn-ghost" aria-label={`Decline ${f.user.display_name}`} onClick={() => answer(f, false)}><Icon name="close" size={18} /></button>
                </div>
              ))}
            </div>
            <LoadMore hasMore={incoming.hasMore} loading={incoming.loading} onClick={incoming.loadMore} shown={incoming.items.length} total={incoming.total} />
          </>
        )}

        <span className="lbl">Friends{friends.total ? ` · ${friends.total}` : ""}</span>
        {friends.total > 5 && (
          <ListControls q={friends.q} setQ={friends.setQ} sort={friends.sort} setSort={friends.setSort} placeholder="Search friends…"
            sorts={[{ value: "display_name", label: "Name" }, { value: "-created_at", label: "Newest" }]} />
        )}
        <div className="card flush">
          {friends.items.map((f) => (
            <Link key={f.id} className="list-row" to={`/u/${f.user.handle}`} style={{ minHeight: 64 }}>
              <Avatar name={f.user.display_name} url={f.user.avatar_url} />
              <span className="grow">{f.user.display_name}<span className="sub m">@{f.user.handle}</span></span>
              <Icon name="chevron-right" size={20} className="trail" />
            </Link>
          ))}
          {!friends.loading && friends.total === 0 && (
            <p className="muted small" style={{ padding: 16 }}>
              {friends.q ? "No friends match." : "No friends yet. People who join with your invite become friends automatically, or tap Add friend on someone's profile. Friends see the contact details you share with friends."}
            </p>
          )}
        </div>
        <LoadMore hasMore={friends.hasMore} loading={friends.loading} onClick={friends.loadMore} shown={friends.items.length} total={friends.total} />

        {outgoing.total > 0 && (
          <>
            <span className="lbl">Requests you sent</span>
            <div className="card flush">
              {outgoing.items.map((f) => (
                <div key={f.id} className="list-row" style={{ minHeight: 64 }}>
                  <Avatar name={f.user.display_name} url={f.user.avatar_url} />
                  <span className="grow">{f.user.display_name}<span className="sub">Sent {ago(f.created_at)}</span></span>
                  <button className="btn btn-sm btn-ghost" onClick={() => answer(f, false)}>Cancel</button>
                </div>
              ))}
            </div>
            <LoadMore hasMore={outgoing.hasMore} loading={outgoing.loading} onClick={outgoing.loadMore} shown={outgoing.items.length} total={outgoing.total} />
          </>
        )}

        <span className="lbl">Invite</span>
        <p className="muted" style={{ fontSize: 15, lineHeight: 1.5 }}>Invite someone. They get their own space, and you become friends when they join.</p>
        <form className="stack" onSubmit={invite}>
          <label className="field">
            <span className="field-label">Email address</span>
            <input className="input" type="email" placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <button className="btn btn-primary btn-block" disabled={busy}><Icon name="mail" size={20} />Invite</button>
          <span className="xs muted">Creates a personal link only they can use, then opens your share sheet.</span>
        </form>

        <div className="card stack">
          <span style={{ fontSize: 15, fontWeight: 600 }}>Or share an invite link</span>
          {link ? (
            <div className="link-box">
              <span className="m">{link.url.replace(/^https?:\/\//, "")}</span>
              <button className="btn btn-sm" onClick={async () => { await navigator.clipboard.writeText(link.url); toast("Invite link copied"); }}>
                <Icon name="copy" size={20} />Copy
              </button>
            </div>
          ) : (
            <button className="btn" onClick={makeLink}><Icon name="share" size={20} />Create a link for up to 10 people</button>
          )}
        </div>

        {invites.total > 0 && (
          <>
            <span className="lbl">Sent</span>
            {invites.total > 5 && (
              <ListControls q={invites.q} setQ={invites.setQ} sort={invites.sort} setSort={invites.setSort} placeholder="Search by email…"
                sorts={[{ value: "-created_at", label: "Newest" }, { value: "created_at", label: "Oldest" }, { value: "-uses", label: "Most used" }]} />
            )}
            <div className="card flush">
              {invites.items.map((inv) => {
                const s = status(inv);
                return (
                  <div key={inv.id} className="list-row" style={{ minHeight: 64 }}>
                    <span className="avatar" style={{ width: 40, height: 40, background: "var(--omni-surface-2)", color: "var(--omni-text-2)", fontSize: 14 }}>
                      {(inv.email ?? inv.note ?? "?")[0].toUpperCase()}
                    </span>
                    <span className="grow" style={{ minWidth: 0 }}>
                      <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>{inv.email ?? inv.note ?? "Invite link"}</span>
                      <span className="sub">Sent {ago(inv.created_at)}{inv.max_uses > 1 ? ` · ${inv.uses}/${inv.max_uses} joined` : ""}</span>
                    </span>
                    {s.label === "Pending" ? (
                      <button className="btn btn-sm btn-ghost" aria-label="Share again" onClick={() => share(inv)}><Icon name="share" size={18} /></button>
                    ) : null}
                    <span className={`pill ${s.tone}`}>{s.label}</span>
                  </div>
                );
              })}
            </div>
            <LoadMore hasMore={invites.hasMore} loading={invites.loading} onClick={invites.loadMore} shown={invites.items.length} total={invites.total} />
          </>
        )}

        {joined.total > 0 && (
          <>
            <span className="lbl">Joined thanks to you</span>
            <div className="card flush">
              {joined.items.map((u) => (
                <Link key={u.id} className="list-row" to={`/u/${u.handle}`} style={{ minHeight: 64 }}>
                  <Avatar name={u.display_name} url={u.avatar_url} />
                  <span className="grow">{u.display_name}<span className="sub m">@{u.handle}</span></span>
                  <span className="pill ok">Joined</span>
                </Link>
              ))}
            </div>
            <LoadMore hasMore={joined.hasMore} loading={joined.loading} onClick={joined.loadMore} shown={joined.items.length} total={joined.total} />
          </>
        )}
      </Page>
    </>
  );
}
